
/**
 * Component Version Registry
 * 
 * Central control for which version of each component to use.
 * This allows easy switching between stable/working/WIP versions.
 */

export type ComponentVersion = 'stable' | 'working' | 'wip';

export interface ComponentVersionConfig {
  CompleteLandingPage: ComponentVersion;
  NewBrandLandingPage: ComponentVersion;
  BrigAIChatDemo: ComponentVersion;
  BrigAIDemoSection: ComponentVersion;
  MarketingNavigation: ComponentVersion;
  MarketingFooter: ComponentVersion;
  EnhancedBetaSignupForm: ComponentVersion;
  HeroSection: ComponentVersion;
  AgentHierarchyShowcase: ComponentVersion;
  FeaturesSection: ComponentVersion;
  ProblemSolutionSection: ComponentVersion;
  ROIMetricsSection: ComponentVersion;
  SocialProofSection: ComponentVersion;
  CTASection: ComponentVersion;
}

/**
 * Current active versions for all landing page components
 * 
 * Change these values to switch between versions:
 * - 'stable': Known good baseline (.stable.tsx files)
 * - 'working': Current active version (.tsx files) 
 * - 'wip': Work in progress version (.wip.tsx files)
 */
export const COMPONENT_VERSIONS: ComponentVersionConfig = {
  CompleteLandingPage: 'working', // VERSION 3.0: Brand-optimized landing page with AI hierarchy
  NewBrandLandingPage: 'working', // VERSION 4.2: Added MarketingAgentHierarchy showcase section
  BrigAIChatDemo: 'working', 
  BrigAIDemoSection: 'stable',
  MarketingNavigation: 'working', // VERSION 3.0: Consistent brand colors
  MarketingFooter: 'working', // VERSION 3.0: Consistent brand colors
  EnhancedBetaSignupForm: 'stable', // VERSION 1.1: Complete beta signup system
  HeroSection: 'working', // VERSION 3.0: Brand-optimized hero section
  AgentHierarchyShowcase: 'working', // VERSION 3.0: Real AI hierarchy image
  FeaturesSection: 'working', // VERSION 3.0: Brand color consistency
  ProblemSolutionSection: 'working', // VERSION 3.0: Optimized content
  ROIMetricsSection: 'working', // VERSION 3.0: Brand color consistency
  SocialProofSection: 'working', // VERSION 4.0: Fixed testimonials structure
  CTASection: 'working' // VERSION 3.0: Brand color consistency
};

/**
 * Version metadata for tracking and documentation
 */
export const VERSION_METADATA = {
  version: '4.2',
  lastUpdated: new Date().toISOString(),
  baseline: 'working',
  notes: 'VERSION 4.2: Added MarketingAgentHierarchy showcase section to NewBrandLandingPage. Positioned strategically between problem/solution and ROI metrics for maximum impact.',
  changelog: 'See COMPONENT_CHANGELOG.md for detailed history'
};

/**
 * Utility function to get the correct import path for a component
 */
export function getComponentPath(componentName: keyof ComponentVersionConfig): string {
  const version = COMPONENT_VERSIONS[componentName];
  const basePath = `./landing/${componentName}`;
  
  switch (version) {
    case 'stable':
      return `${basePath}.stable`;
    case 'wip':
      return `${basePath}.wip`;
    case 'working':
    default:
      return basePath;
  }
}

/**
 * Emergency rollback function - revert all to stable
 */
export function rollbackToStable(): ComponentVersionConfig {
  return {
    CompleteLandingPage: 'stable',
    NewBrandLandingPage: 'stable',
    BrigAIChatDemo: 'stable',
    BrigAIDemoSection: 'stable',
    MarketingNavigation: 'stable',
    MarketingFooter: 'stable',
    EnhancedBetaSignupForm: 'stable',
    HeroSection: 'stable',
    AgentHierarchyShowcase: 'stable',
    FeaturesSection: 'stable',
    ProblemSolutionSection: 'stable',
    ROIMetricsSection: 'stable',
    SocialProofSection: 'stable',
    CTASection: 'stable'
  };
}

/**
 * Validation function to ensure version files exist
 */
export function validateVersions(): { valid: boolean; missing: string[] } {
  // All stable versions have been created and saved for v4.0
  // Version 4.0 working versions are now active with fixed testimonials structure
  return {
    valid: true,
    missing: []
  };
}
